import { useEffect, useRef, useState } from "react";
import type { ModeConfig } from "../state/modeConfig";
import type { usePlayerState } from "../hooks/usePlayerState";
import { useGameSession } from "../session/useGameSession";
import { generateLetterSequence } from "../logic/sequences";
import {
  MathProblem,
  generateMathSet,
  countCorrect,
} from "../logic/math";
import { calcMathPenalty } from "../logic/scoring";
import { TopBar } from "../components/TopBar";
import { Instructions } from "../screens/Instructions";
import { Results } from "../screens/Results";
import { FeedbackOverlay } from "../components/FeedbackOverlay";
import { CountdownOverlay } from "../components/CountdownOverlay";
import { MODE_COUNTDOWN_MS } from "../state/modeConfig";
import { LetterPad } from "../components/LetterPad";
import { TrueFalseButtons } from "../components/TrueFalseButtons";
import { useTranslation } from "../i18n/useTranslation";

type SubPhase = "showLetter" | "showMath" | "recall";
const LETTER_SHOW_MS = 1500;

interface DualTaskProps {
  config: ModeConfig;
  player: ReturnType<typeof usePlayerState>;
  onExit: () => void;
}

export function DualTask({ config, player, onExit }: DualTaskProps) {
  const game = useGameSession(config.modeId, player);
  const { session } = game;
  const { t } = useTranslation();

  const [letters, setLetters] = useState<string[]>([]);
  const [problems, setProblems] = useState<MathProblem[]>([]);
  const [pairIndex, setPairIndex] = useState(0);
  const [subPhase, setSubPhase] = useState<SubPhase>("showLetter");
  const [playerInput, setPlayerInput] = useState<string[]>([]);
  const [mathFlash, setMathFlash] = useState<{
    correct: boolean;
    penalty: number;
  } | null>(null);
  const [, forceRerender] = useState(0);
  const recallSecondsRef = useRef(0);

  const lastRoundRef = useRef(-1);
  useEffect(() => {
    if (session.phase.kind !== "playing") return;
    if (lastRoundRef.current === session.roundNum) return;
    lastRoundRef.current = session.roundNum;
    startRound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.phase, session.roundNum]);

  function startRound(): void {
    const n = session.difficulty;
    setLetters(generateLetterSequence(n));
    setProblems(generateMathSet(n, session.difficulty));
    setPairIndex(0);
    setSubPhase("showLetter");
    setPlayerInput([]);
    setMathFlash(null);
    recallSecondsRef.current = Math.max(15, 25 - session.difficulty);
  }

  useEffect(() => {
    if (subPhase !== "showLetter") return;
    const id = window.setTimeout(() => setSubPhase("showMath"), LETTER_SHOW_MS);
    return () => window.clearTimeout(id);
  }, [subPhase, pairIndex]);

  useEffect(() => {
    if (subPhase !== "recall") return;
    const id = window.setInterval(() => {
      recallSecondsRef.current -= 1;
      forceRerender((x) => x + 1);
      if (recallSecondsRef.current <= 0) {
        window.clearInterval(id);
        submit();
      }
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPhase]);

  function answerMath(answer: boolean): void {
    if (subPhase !== "showMath" || pairIndex >= problems.length) return;
    const p = problems[pairIndex];
    p.playerAnswer = answer;
    const correct = p.answeredCorrectly;
    if (!correct) {
      const penalty = calcMathPenalty(session.streak.current, session.difficulty);
      game.applyMidRoundPenalty(penalty);
      setMathFlash({ correct: false, penalty });
    } else {
      setMathFlash({ correct: true, penalty: 0 });
    }
    window.setTimeout(() => setMathFlash(null), 600);

    const next = pairIndex + 1;
    if (next < letters.length) {
      setPairIndex(next);
      setSubPhase("showLetter");
    } else {
      setSubPhase("recall");
    }
  }

  function handleLetter(letter: string): void {
    if (subPhase !== "recall" || playerInput.length >= letters.length) return;
    setPlayerInput([...playerInput, letter.toUpperCase()]);
  }
  function handleBackspace(): void {
    if (subPhase !== "recall" || playerInput.length === 0) return;
    setPlayerInput(playerInput.slice(0, -1));
  }
  function submit(): void {
    if (subPhase !== "recall") return;
    const lettersOk =
      playerInput.length === letters.length &&
      playerInput.every((v, i) => v === letters[i]);
    const mathOk =
      letters.length > 0 && countCorrect(problems) / letters.length >= 0.5;
    game.submitAnswer(lettersOk && mathOk);
  }

  if (session.phase.kind === "instructions") {
    return (
      <Instructions
        config={config}
        difficulty={session.difficulty}
        highScore={session.playerState.modeHighScores[config.modeId] ?? 0}
        instructions={[
          t("dualTask.instr.show", { n: session.difficulty }),
          t("dualTask.instr.math"),
          t("dualTask.instr.penalty"),
          t("dualTask.instr.recall"),
        ]}
        difficultyDescription={t("dualTask.diffDesc", { n: session.difficulty })}
        onStart={game.startPlaying}
        onBack={onExit}
        onDifficultyChange={game.setDifficulty}
      />
    );
  }
  if (session.phase.kind === "results") {
    return (
      <Results
        modeName={t(`mode.${config.modeId}.name`)}
        result={session.sessionResult}
        level={session.playerState.level}
        xpProgressFrac={session.playerState.xpProgressFrac}
        onContinue={onExit}
      />
    );
  }

  const currentLetter = letters[pairIndex];
  const currentMath = problems[pairIndex];
  const mathCorrectCount = countCorrect(problems);

  return (
    <main className="min-h-screen flex flex-col relative">
      <TopBar
        modeName={t(`mode.${config.modeId}.name`)}
        score={session.score}
        streak={session.streak.current}
        roundNumber={Math.min(session.roundNum + 1, session.maxRounds)}
        totalRounds={session.maxRounds}
      />

      <section className="flex-1 flex flex-col items-center justify-center px-6 gap-6 relative">
        {subPhase === "showLetter" && currentLetter !== undefined && (
          <>
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-cyan">
              {t("dualTask.rememberLetter")}
            </span>
            <div className="w-36 h-36 rounded-full border-4 border-green flex items-center justify-center">
              <span className="text-7xl font-bold font-mono text-fg-0">
                {currentLetter}
              </span>
            </div>
            <span className="text-fg-3 text-sm">
              {t("dualTask.letterOf", {
                index: pairIndex + 1,
                total: letters.length,
              })}
            </span>
          </>
        )}

        {subPhase === "showMath" && currentMath && (
          <>
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-amber">
              {t("dualTask.trueOrFalse")}
            </span>
            <span className="text-4xl font-bold font-mono text-fg-0">
              {currentMath.text}
            </span>
            {mathFlash && (
              <span
                className={`text-sm font-semibold ${mathFlash.correct ? "text-green" : "text-red"}`}
              >
                {mathFlash.correct
                  ? t("common.correct")
                  : t("dualTask.wrongMath", { penalty: mathFlash.penalty })}
              </span>
            )}
            <TrueFalseButtons onAnswer={answerMath} />
          </>
        )}

        {subPhase === "recall" && (
          <div className="w-full flex flex-col items-center gap-4">
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-fg-2">
              {t("dualTask.recall")}
            </span>
            <div className="flex gap-2 justify-center flex-wrap max-w-md">
              {letters.map((_, i) => {
                const filled = i < playerInput.length;
                const active = i === playerInput.length;
                return (
                  <div
                    key={i}
                    className={[
                      "w-12 h-12 rounded-lg bg-bg-2 border-2 flex items-center justify-center text-xl font-bold font-mono",
                      filled
                        ? "border-cyan text-fg-0"
                        : active
                          ? "border-cyan/60 text-fg-3"
                          : "border-[var(--color-line)] text-fg-3",
                    ].join(" ")}
                  >
                    {filled ? playerInput[i] : ""}
                  </div>
                );
              })}
            </div>
            <span className="text-amber text-xs">
              {t("dualTask.mathScore", {
                correct: mathCorrectCount,
                total: problems.length,
              })}
            </span>
            {recallSecondsRef.current <= 5 && (
              <span className="text-red text-sm">
                {t("common.hurry", { seconds: recallSecondsRef.current })}
              </span>
            )}
            <LetterPad
              onLetter={handleLetter}
              onBackspace={handleBackspace}
              onSubmit={submit}
              submitDisabled={playerInput.length === 0}
            />
          </div>
        )}

        {session.phase.kind === "countdown" && (
          <CountdownOverlay
            durationMs={MODE_COUNTDOWN_MS[config.modeId] ?? 0}
            onSkip={game.finishCountdown}
          />
        )}

        {session.phase.kind === "feedback" && (
          <FeedbackOverlay
            correct={session.phase.correct}
            detail={
              session.phase.correct
                ? undefined
                : t("common.answer", { answer: letters.join(" ") })
            }
          />
        )}
      </section>
    </main>
  );
}
